import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft, UserCircle, Loader2, ShieldCheck, KeyRound, Save,
  Mail, MessageSquare, AlertTriangle, Clock, AtSign, Eye, EyeOff, Phone,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const fmtRelDays = (days) => {
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

export function AdminProfilePage() {
  const { admin, loading } = useAdminAuth();
  const [profile, setProfile] = useState(null);
  const [fetching, setFetching] = useState(true);
  // Reflects the live WhatsApp delivery state for the dev banner.
  // True = MSG91 fully configured + selected → real OTPs go out.
  const [whatsappLive, setWhatsappLive] = useState(false);

  // Contacts form
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [savingContacts, setSavingContacts] = useState(false);

  // Password change flow (EITHER channel)
  const [pwOpen, setPwOpen] = useState(false);
  const [pwStage, setPwStage] = useState('initiate'); // 'initiate' | 'verify'
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwChannel, setPwChannel] = useState('email');
  const [pwOtp, setPwOtp] = useState('');
  const [otpInfo, setOtpInfo] = useState(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Email change flow (BOTH channels — dual OTP)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailStage, setEmailStage] = useState('initiate'); // 'initiate' | 'verify'
  const [emailCurrentPw, setEmailCurrentPw] = useState('');
  const [showEmailCurrentPw, setShowEmailCurrentPw] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailEmailOtp, setEmailEmailOtp] = useState('');
  const [emailWaOtp, setEmailWaOtp] = useState('');
  const [emailOtpInfo, setEmailOtpInfo] = useState(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  // Company-phone change flow (Password + EMAIL OTP — single channel)
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneStage, setPhoneStage] = useState('initiate'); // 'initiate' | 'verify'
  const [phoneCurrentPw, setPhoneCurrentPw] = useState('');
  const [showPhoneCurrentPw, setShowPhoneCurrentPw] = useState(false);
  const [newCompanyPhone, setNewCompanyPhone] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpInfo, setPhoneOtpInfo] = useState(null);
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);

  const loadProfile = async () => {
    setFetching(true);
    try {
      const [pRes, waRes] = await Promise.all([
        adminApi.get('/admin/profile/me'),
        adminApi
          .get('/admin/whatsapp-integrations')
          .catch(() => ({ data: null })),
      ]);
      setProfile(pRes.data);
      setRecoveryEmail(pRes.data.recovery_email || '');
      setWhatsappNumber(pRes.data.whatsapp_number || '');
      setWhatsappLive(
        !!waRes.data
          && waRes.data.provider === 'msg91'
          && !!waRes.data.msg91?.is_auth_key_configured,
      );
    } catch {
      toast.error('Could not load profile');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { if (admin) loadProfile(); }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const saveContacts = async () => {
    setSavingContacts(true);
    try {
      const r = await adminApi.put('/admin/profile/contacts', {
        recovery_email: recoveryEmail || null,
        whatsapp_number: whatsappNumber || null,
      });
      setProfile(r.data);
      toast.success('Contacts updated');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save contacts');
    } finally {
      setSavingContacts(false);
    }
  };

  const openChangePassword = () => {
    setPwStage('initiate');
    setCurrentPw('');
    setNewPw('');
    setPwChannel('email');
    setPwOtp('');
    setOtpInfo(null);
    setShowCurrentPw(false);
    setShowNewPw(false);
    setPwOpen(true);
  };

  const initiatePw = async () => {
    if (newPw.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwChannel === 'whatsapp' && !profile?.whatsapp_number) {
      toast.error('Add a WhatsApp number under Recovery Contacts first.');
      return;
    }
    setPwSubmitting(true);
    try {
      const r = await adminApi.post('/admin/profile/change-password/initiate', {
        current_password: currentPw,
        new_password: newPw,
        channel: pwChannel,
      });
      setOtpInfo(r.data);
      setPwStage('verify');
      toast.success(`OTP dispatched via ${pwChannel}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not initiate change');
    } finally {
      setPwSubmitting(false);
    }
  };

  const verifyPw = async () => {
    if (pwOtp.length !== 6) {
      toast.error('OTP must be 6 digits');
      return;
    }
    setPwSubmitting(true);
    try {
      await adminApi.post('/admin/profile/change-password/verify', {
        channel: pwChannel,
        otp: pwOtp,
      });
      toast.success('Password changed successfully');
      setPwOpen(false);
      loadProfile();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not verify OTP');
    } finally {
      setPwSubmitting(false);
    }
  };

  // ----- Email change (dual OTP) ---------------------------------------
  const openChangeEmail = () => {
    setEmailStage('initiate');
    setEmailCurrentPw('');
    setShowEmailCurrentPw(false);
    setNewEmail('');
    setEmailEmailOtp('');
    setEmailWaOtp('');
    setEmailOtpInfo(null);
    setEmailOpen(true);
  };

  const initiateEmail = async () => {
    if (!newEmail || !/^\S+@\S+\.\S+$/.test(newEmail)) {
      toast.error('Enter a valid new email.');
      return;
    }
    if (!profile?.whatsapp_number) {
      toast.error('Email rotation requires a saved WhatsApp number. Add one under Recovery Contacts.');
      return;
    }
    setEmailSubmitting(true);
    try {
      const r = await adminApi.post('/admin/profile/change-email/initiate', {
        current_password: emailCurrentPw,
        new_email: newEmail,
      });
      setEmailOtpInfo(r.data);
      setEmailStage('verify');
      toast.success('OTPs dispatched to both recovery channels');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not initiate change');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const verifyEmail = async () => {
    if (emailEmailOtp.length !== 6 || emailWaOtp.length !== 6) {
      toast.error('Both OTPs must be 6 digits.');
      return;
    }
    setEmailSubmitting(true);
    try {
      await adminApi.post('/admin/profile/change-email/verify', {
        email_otp: emailEmailOtp,
        whatsapp_otp: emailWaOtp,
      });
      toast.success('Login email updated.');
      setEmailOpen(false);
      loadProfile();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not verify OTPs');
    } finally {
      setEmailSubmitting(false);
    }
  };

  // ----- Company phone change (Password + Email OTP) ------------------
  const openChangePhone = () => {
    setPhoneStage('initiate');
    setPhoneCurrentPw('');
    setShowPhoneCurrentPw(false);
    setNewCompanyPhone('');
    setPhoneOtp('');
    setPhoneOtpInfo(null);
    setPhoneOpen(true);
  };

  const initiatePhone = async () => {
    if (!newCompanyPhone || !/^\+\d{9,19}$/.test(newCompanyPhone)) {
      toast.error('Enter a phone with country code (e.g. +919876543210).');
      return;
    }
    if (newCompanyPhone === (profile?.company_phone || '')) {
      toast.error('New phone must differ from the current one.');
      return;
    }
    setPhoneSubmitting(true);
    try {
      const r = await adminApi.post('/admin/profile/change-company-phone/initiate', {
        current_password: phoneCurrentPw,
        new_company_phone: newCompanyPhone,
      });
      setPhoneOtpInfo(r.data);
      setPhoneStage('verify');
      toast.success('OTP sent to your login email.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not initiate change');
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const verifyPhone = async () => {
    if (phoneOtp.length !== 6) {
      toast.error('OTP must be 6 digits');
      return;
    }
    setPhoneSubmitting(true);
    try {
      await adminApi.post('/admin/profile/change-company-phone/verify', {
        otp: phoneOtp,
      });
      toast.success('Company phone updated.');
      setPhoneOpen(false);
      loadProfile();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not verify OTP');
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const expiresIn = profile?.password_expires_in_days;
  const expired = profile?.password_expired;

  return (
    <div
      data-testid="admin-profile-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" data-testid="profile-back" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <UserCircle className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="text-white font-semibold">My Profile</div>
            <div className="text-xs text-slate-400">SuperAdmin account & password rotation</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6 space-y-5">
        {fetching ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : profile && (
          <>
            {/* Identity */}
            <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5" data-testid="profile-identity">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <div className="text-white font-semibold text-lg">{profile.name}</div>
                  <div className="text-sm text-slate-400">{profile.email}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openChangeEmail}
                  data-testid="profile-change-email-btn"
                  className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                >
                  <AtSign className="w-3.5 h-3.5 mr-1.5" /> Change email
                </Button>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300">
                  {profile.role}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                Email rotation is a high-risk change — it requires BOTH a recovery-email OTP and a WhatsApp OTP.
              </p>
            </div>

            {/* Password rotation banner */}
            <div
              data-testid="profile-password-banner"
              className={`rounded-xl border p-4 flex items-start gap-3 ${
                expired
                  ? 'bg-red-500/10 border-red-500/40'
                  : (expiresIn != null && expiresIn <= 14)
                    ? 'bg-amber-500/10 border-amber-500/40'
                    : 'bg-slate-900/70 border-slate-800'
              }`}
            >
              {expired
                ? <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                : <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />}
              <div className="flex-1">
                <div className={`text-sm font-semibold ${expired ? 'text-red-300' : 'text-white'}`}>
                  {expired
                    ? 'Your password has expired'
                    : expiresIn != null && expiresIn <= 14
                      ? `Password expires in ${expiresIn} days`
                      : 'Password rotation policy'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Last set {fmtRelDays(profile.password_age_days)} · 90-day rotation enforced.
                  {expired && ' Change it now to regain full access.'}
                </div>
              </div>
              <Button
                size="sm"
                onClick={openChangePassword}
                data-testid="profile-change-password-btn"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Change password
              </Button>
            </div>

            {/* Contacts */}
            <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-4" data-testid="profile-contacts">
              <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Recovery contacts</h2>
              <p className="text-xs text-slate-400">
                These addresses receive OTPs whenever you change your password or authorise a privileged action.
              </p>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Recovery email
                </label>
                <Input
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="recovery@example.com"
                  data-testid="profile-recovery-email"
                  className="bg-slate-800/50 border-slate-700 text-white"
                />
              </div>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" /> WhatsApp number (with country code)
                </label>
                <Input
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="+919876543210"
                  data-testid="profile-whatsapp"
                  className="bg-slate-800/50 border-slate-700 text-white font-mono"
                />
                <div className="text-[11px] text-amber-300/80 mt-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {whatsappLive ? (
                    <>
                      WhatsApp delivery is <span className="text-emerald-300 font-semibold">LIVE</span> via MSG91.{' '}
                      <Link to="/admin/whatsapp-integrations" className="underline hover:text-amber-200">
                        Manage integration →
                      </Link>
                    </>
                  ) : (
                    <>
                      WhatsApp delivery is currently mocked — the OTP is always <code className="font-mono">123456</code>.{' '}
                      <Link to="/admin/whatsapp-integrations" className="underline hover:text-amber-200">
                        Configure MSG91 →
                      </Link>
                    </>
                  )}
                </div>
              </div>

              <Button
                onClick={saveContacts}
                disabled={savingContacts}
                data-testid="profile-save-contacts"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {savingContacts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save contacts
              </Button>
            </div>

            {/* Business phone (company_phone) — Password + Email OTP gated */}
            <div
              className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-3"
              data-testid="profile-company-phone"
            >
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
                  Business contact phone
                </h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openChangePhone}
                  data-testid="profile-change-phone-btn"
                  className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                >
                  <Phone className="w-3.5 h-3.5 mr-1.5" /> Change phone
                </Button>
              </div>
              <p className="text-xs text-slate-400">
                Used by SuperAdmin Part-D OTP routing (Payments, Webhooks, GSP saves)
                and printed on invoices. Editing this is a sensitive action — requires
                your current password and an Email OTP.
              </p>
              <div className="rounded-lg bg-slate-800/50 border border-slate-700/60 px-3 py-2.5 flex items-center gap-3">
                <Phone className="w-4 h-4 text-amber-400 shrink-0" />
                <div
                  className="font-mono text-sm text-white tracking-wide"
                  data-testid="profile-company-phone-value"
                >
                  {profile.company_phone || (
                    <span className="text-slate-500 italic font-sans">Not set</span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Change-password modal — EITHER-channel OTP */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-400" /> Change password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {pwStage === 'initiate'
                ? 'Confirm your current password, pick a new one, and choose where to receive the OTP.'
                : 'Enter the 6-digit code to finalise the change.'}
            </DialogDescription>
          </DialogHeader>

          {pwStage === 'initiate' ? (
            <div className="space-y-3">
              <div className="relative">
                <Input
                  type={showCurrentPw ? 'text' : 'password'}
                  placeholder="Current password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  data-testid="change-password-current"
                  className="pr-10 bg-slate-800/50 border-slate-700 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPw((v) => !v)}
                  data-testid="change-password-current-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400"
                >
                  {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showNewPw ? 'text' : 'password'}
                  placeholder="New password (min 8 chars)"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  data-testid="change-password-new"
                  className="pr-10 bg-slate-800/50 border-slate-700 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw((v) => !v)}
                  data-testid="change-password-new-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400"
                >
                  {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div>
                <div className="text-xs text-slate-300 mb-1.5">Send OTP via</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPwChannel('email')}
                    data-testid="change-password-channel-email"
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      pwChannel === 'email'
                        ? 'border-amber-400/60 bg-amber-500/10 text-amber-200'
                        : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    <Mail className="w-4 h-4" /> Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setPwChannel('whatsapp')}
                    disabled={!profile?.whatsapp_number}
                    data-testid="change-password-channel-whatsapp"
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      pwChannel === 'whatsapp'
                        ? 'border-amber-400/60 bg-amber-500/10 text-amber-200'
                        : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" /> WhatsApp
                  </button>
                </div>
                {!profile?.whatsapp_number && (
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Add a WhatsApp number under Recovery Contacts to enable that channel.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {otpInfo && (
                <div className="rounded-md bg-slate-800/60 border border-slate-700 p-3 text-xs space-y-1">
                  <div className="text-slate-400">
                    OTP sent via <span className="text-slate-200">{otpInfo.channel}</span> to{' '}
                    <span className="text-slate-200">{otpInfo.sent_to}</span>
                  </div>
                  {otpInfo.whatsapp_is_mocked && otpInfo.channel === 'whatsapp' && (
                    <div className="mt-2 text-amber-300 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> WhatsApp is mocked — use <code className="font-mono">{otpInfo.mock_code_hint}</code>
                    </div>
                  )}
                </div>
              )}
              <Input
                placeholder={`${pwChannel === 'email' ? 'Email' : 'WhatsApp'} OTP (6 digits)`}
                value={pwOtp}
                onChange={(e) => setPwOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="change-password-otp"
                className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
              />
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setPwOpen(false)} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
              Cancel
            </Button>
            {pwStage === 'initiate' ? (
              <Button onClick={initiatePw} disabled={pwSubmitting || !currentPw || !newPw} data-testid="change-password-initiate-submit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                {pwSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send OTP
              </Button>
            ) : (
              <Button onClick={verifyPw} disabled={pwSubmitting || pwOtp.length !== 6} data-testid="change-password-verify-submit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                {pwSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm change
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change-email modal — DUAL-channel OTP (high-risk flow) */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="change-email-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AtSign className="w-4 h-4 text-amber-400" /> Change login email
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {emailStage === 'initiate'
                ? 'Email rotation is the highest-risk identity change. We will dispatch OTPs to BOTH your recovery email AND WhatsApp number — you need to enter both to confirm.'
                : 'Enter both 6-digit codes to finalise the rotation.'}
            </DialogDescription>
          </DialogHeader>

          {emailStage === 'initiate' ? (
            <div className="space-y-3">
              <div className="relative">
                <Input
                  type={showEmailCurrentPw ? 'text' : 'password'}
                  placeholder="Current password"
                  value={emailCurrentPw}
                  onChange={(e) => setEmailCurrentPw(e.target.value)}
                  data-testid="change-email-current-pw"
                  className="pr-10 bg-slate-800/50 border-slate-700 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowEmailCurrentPw((v) => !v)}
                  data-testid="change-email-current-pw-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400"
                >
                  {showEmailCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Input
                type="email"
                placeholder="New login email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                data-testid="change-email-new"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
              <div className="rounded-md bg-slate-800/40 border border-slate-700/50 px-3 py-2 text-[11px] text-slate-400">
                Recovery email and WhatsApp number will remain unchanged. Update them
                separately after the new login email is confirmed.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {emailOtpInfo && (
                <div className="rounded-md bg-slate-800/60 border border-slate-700 p-3 text-xs space-y-1">
                  <div className="text-slate-400">Email OTP sent to <span className="text-slate-200">{emailOtpInfo.email_sent_to}</span></div>
                  <div className="text-slate-400">WhatsApp OTP sent to <span className="text-slate-200">{emailOtpInfo.whatsapp_sent_to}</span></div>
                  <div className="text-slate-400">New login email pending: <span className="text-slate-200">{emailOtpInfo.new_email_preview}</span></div>
                  {emailOtpInfo.whatsapp_is_mocked && (
                    <div className="mt-2 text-amber-300 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> WhatsApp is mocked — use <code className="font-mono">{emailOtpInfo.mock_code_hint}</code>
                    </div>
                  )}
                </div>
              )}
              <Input
                placeholder="Email OTP (6 digits)"
                value={emailEmailOtp}
                onChange={(e) => setEmailEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="change-email-email-otp"
                className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
              />
              <Input
                placeholder="WhatsApp OTP (6 digits)"
                value={emailWaOtp}
                onChange={(e) => setEmailWaOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="change-email-wa-otp"
                className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
              />
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setEmailOpen(false)} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
              Cancel
            </Button>
            {emailStage === 'initiate' ? (
              <Button
                onClick={initiateEmail}
                disabled={emailSubmitting || !emailCurrentPw || !newEmail}
                data-testid="change-email-initiate-submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {emailSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send OTPs
              </Button>
            ) : (
              <Button
                onClick={verifyEmail}
                disabled={emailSubmitting || emailEmailOtp.length !== 6 || emailWaOtp.length !== 6}
                data-testid="change-email-verify-submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {emailSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm change
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change-company-phone modal — Password + EMAIL OTP only */}
      <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
        <DialogContent
          data-testid="change-phone-dialog"
          className="bg-slate-900 border-slate-800 text-slate-100 max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Phone className="w-4 h-4 text-amber-400" /> Change business phone
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {phoneStage === 'initiate'
                ? 'Confirm your current password and enter the new business phone. An Email OTP will be sent to your login email.'
                : 'Enter the 6-digit code we sent to your login email to finalise the update.'}
            </DialogDescription>
          </DialogHeader>

          {phoneStage === 'initiate' ? (
            <div className="space-y-3">
              <div className="relative">
                <Input
                  type={showPhoneCurrentPw ? 'text' : 'password'}
                  placeholder="Current password"
                  value={phoneCurrentPw}
                  onChange={(e) => setPhoneCurrentPw(e.target.value)}
                  data-testid="change-phone-current-pw"
                  className="pr-10 bg-slate-800/50 border-slate-700 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPhoneCurrentPw((v) => !v)}
                  data-testid="change-phone-current-pw-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400"
                >
                  {showPhoneCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Input
                placeholder="+919876543210"
                value={newCompanyPhone}
                onChange={(e) => setNewCompanyPhone(e.target.value)}
                data-testid="change-phone-new"
                className="bg-slate-800/50 border-slate-700 text-white font-mono"
              />
              <div className="rounded-md bg-slate-800/40 border border-slate-700/50 px-3 py-2 text-[11px] text-slate-400">
                Must include country code (e.g. <code className="font-mono">+91</code> for India).
                Used to route Part-D dual-channel WhatsApp OTPs.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {phoneOtpInfo && (
                <div className="rounded-md bg-slate-800/60 border border-slate-700 p-3 text-xs space-y-1">
                  <div className="text-slate-400">
                    Email OTP sent to <span className="text-slate-200">{phoneOtpInfo.sent_to}</span>
                  </div>
                  <div className="text-slate-400">
                    New phone pending: <span className="text-slate-200 font-mono">{phoneOtpInfo.new_phone_preview}</span>
                  </div>
                </div>
              )}
              <Input
                placeholder="Email OTP (6 digits)"
                value={phoneOtp}
                onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="change-phone-otp"
                className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setPhoneOpen(false)}
              variant="outline"
              className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
            >
              Cancel
            </Button>
            {phoneStage === 'initiate' ? (
              <Button
                onClick={initiatePhone}
                disabled={phoneSubmitting || !phoneCurrentPw || !newCompanyPhone}
                data-testid="change-phone-initiate-submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {phoneSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send OTP
              </Button>
            ) : (
              <Button
                onClick={verifyPhone}
                disabled={phoneSubmitting || phoneOtp.length !== 6}
                data-testid="change-phone-verify-submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {phoneSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm change
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
